import { ESIMCardStatus, ICloudService, ICommonRes } from '../';

export type IPlanFilter = 'all'
    | 'inactive'//无套餐或套餐已过期
    | 'active'//套餐仍在有效期
    | 'unbound'//设备为且套餐仍在有效期
    | 'inactive_activeDataPack' //补充包仍有效
    | 'active_expiringSoon'//即将过期的套餐优先显示
    | 'active_dataRemaining'// 流量即将用完
    | 'active_dataDepleted'; // 流量已用完

export type IOriginalPlanFilter = 'all'
    |'no_or_expired' //无套餐或套餐已过期
    |'active' //套餐仍在有效期
    |'unbound' //设备为且套餐仍在有效期
    |'add_on_active' //补充包仍有效
    |'expired_soon_prefer' //即将过期的套餐优先显示
    |'traffic_run_out_soon' //流量即将用完
    |'traffic_run_out'; //流量已用完

const planFilterMap: Record<IOriginalPlanFilter, IPlanFilter> = {
    all: 'all',
    no_or_expired: 'inactive',
    active: 'active',
    unbound: 'unbound',
    add_on_active: 'inactive_activeDataPack',
    expired_soon_prefer: 'active_expiringSoon',
    traffic_run_out: 'active_dataDepleted',
    traffic_run_out_soon: 'active_dataRemaining'
};

export function getPlanFilter( originalFilter: IOriginalPlanFilter ): IPlanFilter {

    return planFilterMap[originalFilter];

}

export enum ESimCardModule {

    SIM_CARD = 'simCard',
    LAST_USED_DEVICE = 'lastUsedDevice',
    CLOUD_SERVICES = 'cloudServices',
    CURRENT_DATA_USAGE = 'currentDataUsage'
}

export interface IGetSimCardList_BodyArgs {

    /**
     * SIM卡唯一标识
     * 取值范围：string(20)[1,100]
     */
    iccids?: string[];

    /**
     * 返回的指定返回的数据模块
     * 可选的有:
     * simCard
     * lastUsedDevice
     * lastUsedDevice
     * cloudServices
     *
     */
    includes: ESimCardModule[];

    /**
     * 每页查询数量限制
     *
     * 取值范围：整数。范围： 10 ~ 100
     */
    limit: number;

    /**
     * 分页页码
     *
     * 取值范围：整数。范围：1 ~ 1000
     */
    page: number;

    lang?: string;

    /**
     * 排序策略:
     * simCardList: SimCard列表排序规则
     * selectSimCard: 选择simCard页面
     *
     */
    sortStrategy: 'simCardList' | 'selectSimCard';

    /**
     * 复合筛选条件
     *
     * all: (默认) 所有设备/卡
     *
     * --- 一级筛选 ---
     * active: 套餐在有效期内
     * inactive: 无套餐或套餐已过期
     * unbound: 已解绑但套餐有效
     *
     * --- 二级筛选 (基于一级筛选) ---
     * active_expiringSoon: 套餐有效且即将过期
     * active_dataDepleted: 套餐有效但流量耗尽
     * active_dataRemaining: 套餐有效且流量有剩余
     * inactive_activeDataPack: 套餐无效但流量包有效
     *
     * @default 'all'
     */
    planFilter?: IPlanFilter;

}

export interface ISimCardItem {
    /**
     * Sim卡id
     * 取值范围: safe_uint
     */
    id: string;

    /**
     * Sim卡卡号
     * 取值范围: string(10) 大写字母、数字
     *
     */
    simCardCode: string;

    /**
     * SIM卡唯一标识
     * 取值范围：string(20)
     */
    iccid: string;

    state: ESIMCardStatus;

    /**
     * 卡的模式
     * admin-人工模式
     * auto-自动模式
     */
    mode: 'admin' | 'auto';

    /** 
     * 网络状态
     * normal-正常，不受限制
     * limited-网络受限
     * recovering-恢复中
     */
    trafficLimitStatus: 'normal' | 'limited' | 'recovering';

    /**
     * 最后卡使用流量的国家代号，如 US，如果不知道国家，则显示 unknown
     */
    trafficCountry: string;

    trafficCountryName: string;

}

export interface ILastUsedDeviceItem {
    /**
     * 设备的 UID
     *
     * @type string(16)
     */
    uid: string;

    /**
     * 用户设置的机器名称。
     *
     * @type string(0,128)
     */
    title: string;

    /**
     * 机型名称。
     *
     * @type string(1,32)
     */
    model: {

        /**
         * 型号对外名称。
         *
         * @type string(1,64)
         */
        title: string;
    };
}

export interface ISimCard {

    /**
     * SimCard信息
     */
    simCard?: ISimCardItem;

    /**
     * 上次使用该Sim卡的设备
     * 取值：
     * null：没有使用过
     */
    lastUsedDevice?: ILastUsedDeviceItem | null;

    /**
     * 当前计费周期的流量使用情况
     * 取值范围：
     * null：当前计费周期无套餐
     *
     */
    currentDataUsage?: ICurrentDataUsageView | null;

    /**
     * 设备的云服务id 列表
     * 根据请求 body 中的 includes 是否包含 cloudServices 确定是否返回
     *
     */
    cloudServices?: string[];

    /**
     * 是否是锁卡，是锁卡就传uid
     *
     * */
    esimDevice: string;

    /**
     * 云服务信息
     *
     * */
    services?: Record<string, ICloudService>;

}

export type TGetSimCardListRes = ICommonRes<ISimCard> & {

    /**
     * 云服务信息
     *
     * */
    services?: Record<string, ICloudService>;
};

export interface IGetSimCard_BodyArgs {

    /**
     * SIM卡唯一标识
     * 取值范围：string(20)
     */
    iccid: string;

    /**
     * 返回的指定返回的数据模块
     * 可选的有:
     * simCard
     * lastUsedDevice
     * lastUsedDevice
     * cloudServices
     */
    includes: ESimCardModule[];

    /**
     * 语言 key，需要多语言文案的时候传递
     *
     * 默认使用 en
     */
    'lang'?: string;
    
    /**
     * 是否获取按钮显隐配置
     */
    checkActionConfig?: boolean;
}

export interface IDeleteSimCard_QueryArgs {

    /**
     * SIM卡唯一标识
     * 取值范围：string(20)
     */
    iccid: string;

    /**
     * 手动退款信息
     *
     */
    manualRefundInfo?: {

        /**
         * 退款的支付网关名称
         *
         */
        method: 'paypal';

        /**
         * 退款账号
         *
         * 取值范围: string(0,255)
         */
        account: string;
    };

}

export interface IAddSimCardArgs {

    /**
     * Sim卡卡号
     * 取值范围: string(10) 大写字母、数字
     *
     */
    simCardCode: string;

}

export interface IAddSimCardResponse {
    /**
     * SIM卡唯一标识
     * 取值范围：string(20)
     */
    iccid: string;
}

export interface ICurrentDataUsageView {

    /**
     * 当前计费周期流量总量，单位字节
     *
     */
    totalBytes: string;

    /**
     * 当前计费周期流量使用量，单位字节
     *
     */
    usedBytes: string;
}

export interface IGetPartnerInfoArgs {

    /**
     * 国家/地区
     * @type string(2)
     */
    country: string;

}

interface IPartnerCompanyInfo {

    /**
     * 公司名称
     * @type string(0, 255)
     */
    name?: string;
    /**
     * 公司网站
     * @type string(0, 255)
     */
    website?: string;
    /**
     * 公司电话
     * @type string(0, 64)
     */
    phone?: string;
    /**
     * 公司所在国家/地区
     * @type string(2)
     */
    country: string;

    /**
     * 工作时间
     * @type string(0, 255)
     */
    timeInBusiness?: string;
    /**
     * 销售渠道
     * online-store -在线商店
     * physical-shop - 实体店
     * @type string(0, 255)
     */
    salesChannel?: string | 'online-store' | 'physical-shop' | 'installation-projects';
    /**
     * 公司业务类型
     * distributor - 代理商
     * installer - 安装商
     * reseller - 经销商
     * wholesaler - 批发商
     * dropshipper -代发货人
     * enterprise-buyer -企业买家
     */
    businessType: 'distributor' | 'installer' | 'reseller' | 'wholesaler' | 'dropshipper' | 'enterprise-buyer' | string;
    /**
     * 公司员工数量
     * @type string(0, 64)
     */
    employeeCount: number;
    /**
     * 公司其他合作伙伴
     * @type string(0, 255)
     */
    otherPartners?: string;
    /**
     * 公司介绍
     * @type string(1, 2048)
     */
    introduction: string;
}

/**
 * 模块
 */
export enum EModule {
    /**
     * 摄像头
     */
    CAMERA = 'camera',
    /**
     * SIM卡
     */
    SIM = 'sim',
}

/**
 * 销售数据展示方式
 */
export enum ESalesAmountDisplay {
    /**
     * 不含税
     */
    TAX_EXCLUDED = 'tax_excluded',
    /**
     * 含税
     */
    TAX_INCLUDED = 'tax_included',
}

export interface IGetPartnerInfoRes {
    /**
     * 客户名称
     * @type string(1, 64)
     */
    name: string;

    /**
     * 客户邮箱
     */
    email: string;

    /**
     * 职位
     * @type string(1,255)
     */
    role: string;

    /**
     * 国家
     */
    country: string;

    /**
     * 公司信息
     */
    company?: IPartnerCompanyInfo;

    /**
     * 服务模块
     */
    modules: EModule[];

    /**
     * 销售数据展示方式
     */
    salesAmountDisplay: ESalesAmountDisplay[];
}
